import { ReactNode, useState, useRef, useEffect } from 'react';
import { createPortal } from 'react-dom';

export interface TooltipProps {
  content: string;
  children: ReactNode;
  position?: 'top' | 'bottom' | 'left' | 'right';
  delay?: number;
  className?: string;
}

/**
 * Tooltip component matching PrimeNG's tooltip behavior
 * Shows a styled tooltip on hover with configurable position
 */
export function Tooltip({
  content,
  children,
  position = 'top',
  delay = 200,
  className = '',
}: TooltipProps) {
  const [visible, setVisible] = useState(false);
  const [tooltipPosition, setTooltipPosition] = useState({ top: 0, left: 0 });
  const timeoutRef = useRef<NodeJS.Timeout>();
  const containerRef = useRef<HTMLDivElement>(null);

  const showTooltip = () => {
    if (timeoutRef.current) clearTimeout(timeoutRef.current);
    timeoutRef.current = setTimeout(() => {
      if (containerRef.current) {
        const rect = containerRef.current.getBoundingClientRect();
        switch (position) {
          case 'bottom':
            setTooltipPosition({
              top: rect.bottom,
              left: rect.left + rect.width / 2,
            });
            break;
          case 'left':
            setTooltipPosition({
              top: rect.top + rect.height / 2,
              left: rect.left,
            });
            break;
          case 'right':
            setTooltipPosition({
              top: rect.top + rect.height / 2,
              left: rect.right,
            });
            break;
          case 'top':
          default:
            setTooltipPosition({
              top: rect.top,
              left: rect.left + rect.width / 2,
            });
            break;
        }
      }
      setVisible(true);
    }, delay);
  };

  const hideTooltip = () => {
    if (timeoutRef.current) clearTimeout(timeoutRef.current);
    setVisible(false);
  };

  useEffect(() => {
    return () => {
      if (timeoutRef.current) clearTimeout(timeoutRef.current);
    };
  }, []);

  const getPositionClasses = () => {
    switch (position) {
      case 'top':
        return '-translate-x-1/2 -translate-y-full mb-2';
      case 'bottom':
        return '-translate-x-1/2 mt-2';
      case 'left':
        return '-translate-x-full -translate-y-1/2 mr-2';
      case 'right':
        return '-translate-y-1/2 ml-2';
      default:
        return '-translate-x-1/2 -translate-y-full mb-2';
    }
  };

  const getArrowClasses = () => {
    switch (position) {
      case 'top':
        return 'top-full left-1/2 -translate-x-1/2 border-t-gray-900 dark:border-t-gray-700';
      case 'bottom':
        return 'bottom-full left-1/2 -translate-x-1/2 border-b-gray-900 dark:border-b-gray-700';
      case 'left':
        return 'left-full top-1/2 -translate-y-1/2 border-l-gray-900 dark:border-l-gray-700';
      case 'right':
        return 'right-full top-1/2 -translate-y-1/2 border-r-gray-900 dark:border-r-gray-700';
      default:
        return 'top-full left-1/2 -translate-x-1/2 border-t-gray-900 dark:border-t-gray-700';
    }
  };

  return (
    <div
      ref={containerRef}
      className={`relative inline-flex ${className}`}
      onMouseEnter={showTooltip}
      onMouseLeave={hideTooltip}
    >
      {children}
      {visible &&
        content &&
        createPortal(
          <div
            className={`fixed z-[9999] whitespace-nowrap rounded bg-gray-900 px-2 py-1 text-xs text-white shadow-lg dark:bg-gray-700 ${getPositionClasses()}`}
            style={{
              top: `${tooltipPosition.top}px`,
              left: `${tooltipPosition.left}px`,
            }}
            role="tooltip"
          >
            {content}
            <div className={`absolute h-0 w-0 border-4 border-transparent ${getArrowClasses()}`} />
          </div>,
          document.body
        )}
    </div>
  );
}
